import { useState, useEffect, useCallback } from 'react';
import {
  Plus, X, Loader2, Factory, Target, TrendingUp, AlertTriangle,
  CheckCircle, Clock, ArrowRight, BarChart3, Wrench,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  name: string;
  sku: string;
}

interface PlanItem {
  productId: string;
  productName: string;
  targetQty: number;
  actualQty: number;
}

interface DailyPlan {
  date: string;
  items: PlanItem[];
}

interface QcStatusData {
  stateCounts: Record<string, number>;
  recentTransitions: Array<{
    id: string;
    productName: string;
    productSku: string;
    batchId: string;
    fromState: string;
    toState: string;
    quantity: number;
    reason: string | null;
    createdAt: string;
  }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const QC_STATE_LABELS: Record<string, string> = {
  ready: 'Ready',
  qc_checked: 'QC-1 Checked',
  fully_packed: 'QC-2 Packed',
  dispatched: 'Dispatched',
  damaged: 'Damaged',
  returned: 'Returned',
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ProductionPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [plan, setPlan] = useState<DailyPlan | null>(null);
  const [qcData, setQcData] = useState<QcStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(todayKey());

  // Add Plan Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({ productId: '', targetQty: '' });

  // Actual qty edit
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editActual, setEditActual] = useState('');

  const settingsKey = `production_plan_${selectedDate}`;

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [prodRes, settingsRes, qcRes] = await Promise.all([
        api.get('/products'),
        api.get(`/settings/${settingsKey}`).catch(() => ({ data: { data: { value: null } } })),
        api.get('/warehouse/qc-status').catch(() => ({ data: { data: null } })),
      ]);
      setProducts(prodRes.data.data?.products ?? prodRes.data.data ?? []);

      const settingValue = settingsRes.data?.data?.value;
      if (settingValue && typeof settingValue === 'object' && Array.isArray((settingValue as any).items)) {
        setPlan(settingValue as DailyPlan);
      } else {
        setPlan(null);
      }

      if (qcRes.data?.data) {
        setQcData(qcRes.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load production data');
    } finally {
      setLoading(false);
    }
  }, [settingsKey]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const savePlan = async (updatedPlan: DailyPlan) => {
    try {
      // Use PUT to settings key
      await api.put(`/settings/${settingsKey}`, { value: updatedPlan });
      setPlan(updatedPlan);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save production plan');
    }
  };

  const handleAddItem = async () => {
    if (!newItem.productId || !newItem.targetQty) return;
    setSaving(true);
    const product = products.find((p) => p.id === newItem.productId);
    const item: PlanItem = {
      productId: newItem.productId,
      productName: product?.name || 'Unknown',
      targetQty: parseInt(newItem.targetQty) || 0,
      actualQty: 0,
    };
    const currentItems = plan?.items || [];
    const updatedPlan: DailyPlan = {
      date: selectedDate,
      items: [...currentItems, item],
    };
    await savePlan(updatedPlan);
    setShowModal(false);
    setNewItem({ productId: '', targetQty: '' });
    setSaving(false);
  };

  const handleUpdateActual = async (idx: number) => {
    if (!plan) return;
    const updatedItems = [...plan.items];
    updatedItems[idx] = { ...updatedItems[idx], actualQty: parseInt(editActual) || 0 };
    await savePlan({ ...plan, items: updatedItems });
    setEditingIdx(null);
    setEditActual('');
  };

  const handleRemoveItem = async (idx: number) => {
    if (!plan) return;
    const updatedItems = plan.items.filter((_, i) => i !== idx);
    await savePlan({ ...plan, items: updatedItems });
  };

  // Compute metrics
  const totalTarget = plan?.items.reduce((s, i) => s + i.targetQty, 0) || 0;
  const totalActual = plan?.items.reduce((s, i) => s + i.actualQty, 0) || 0;
  const achievementPct = totalTarget > 0 ? Math.round((totalActual / totalTarget) * 100) : 0;

  const qcFailures = qcData?.recentTransitions.filter((t) => t.toState === 'damaged').length || 0;
  const totalTransitions = qcData?.recentTransitions.length || 0;
  const defectRate = totalTransitions > 0 ? ((qcFailures / totalTransitions) * 100).toFixed(1) : '0.0';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading production data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-red-500">
        <p className="text-sm">{error}</p>
        <button onClick={fetchAll} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Production Planning</h1>
        <p className="text-sm text-gray-500">Plan daily output, track QC stages, and monitor production metrics</p>
      </div>

      {/* Production Metrics */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard
          icon={Target}
          label="Daily Achievement"
          value={`${achievementPct}%`}
          sub={`${totalActual} / ${totalTarget} units`}
          color={achievementPct >= 90 ? 'text-green-600' : achievementPct >= 70 ? 'text-yellow-600' : 'text-red-600'}
        />
        <MetricCard
          icon={Wrench}
          label="Machine Utilization"
          value="--"
          sub="Enter data"
          color="text-gray-400"
          placeholder
        />
        <MetricCard
          icon={AlertTriangle}
          label="Defect Rate"
          value={`${defectRate}%`}
          sub={`${qcFailures} rejects in last 20`}
          color={parseFloat(defectRate) <= 2 ? 'text-green-600' : 'text-red-600'}
        />
        <MetricCard
          icon={Factory}
          label="QC Pipeline"
          value={String((qcData?.stateCounts.ready || 0) + (qcData?.stateCounts.qc_checked || 0) + (qcData?.stateCounts.fully_packed || 0))}
          sub={`${qcData?.stateCounts.ready || 0} ready, ${qcData?.stateCounts.qc_checked || 0} QC-1, ${qcData?.stateCounts.fully_packed || 0} packed`}
          color="text-blue-600"
        />
      </div>

      {/* Daily Plan Section */}
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Daily Plan</h2>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            />
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
          >
            <Plus size={16} />
            Add Plan
          </button>
        </div>

        {!plan || plan.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 py-12 text-gray-400">
            <Factory size={40} className="mb-2" />
            <p className="text-sm">No production plan for {selectedDate}</p>
            <p className="text-xs">Click "Add Plan" to set daily targets</p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3 text-right">Target Qty</th>
                    <th className="px-4 py-3 text-right">Actual Qty</th>
                    <th className="px-4 py-3 text-right">Variance</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {plan.items.map((item, idx) => {
                    const variance = item.actualQty - item.targetQty;
                    const pct = item.targetQty > 0 ? Math.round((item.actualQty / item.targetQty) * 100) : 0;
                    const status = pct >= 100 ? 'Ahead' : pct >= 80 ? 'On Track' : 'Behind';
                    const statusColor = pct >= 100 ? 'bg-green-50 text-green-700' : pct >= 80 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700';

                    return (
                      <tr key={idx} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{item.productName}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{item.targetQty}</td>
                        <td className="px-4 py-3 text-right">
                          {editingIdx === idx ? (
                            <div className="flex items-center justify-end gap-1">
                              <input
                                type="number"
                                value={editActual}
                                onChange={(e) => setEditActual(e.target.value)}
                                className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-[var(--ink-0)] focus:outline-none"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateActual(idx); if (e.key === 'Escape') setEditingIdx(null); }}
                              />
                              <button onClick={() => handleUpdateActual(idx)} className="text-green-600 hover:text-green-800">
                                <CheckCircle size={14} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingIdx(idx); setEditActual(String(item.actualQty)); }}
                              className="font-medium text-[var(--ink-0)] hover:underline"
                            >
                              {item.actualQty}
                            </button>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-right font-medium ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {variance >= 0 ? '+' : ''}{variance}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColor}`}>
                            {status} ({pct}%)
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleRemoveItem(idx)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove from plan"
                          >
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Recent Production Activity */}
      {qcData && qcData.recentTransitions.length > 0 && (
        <div>
          <h2 className="mb-3 text-lg font-semibold text-gray-900">Recent Production Activity</h2>
          <div className="space-y-2">
            {qcData.recentTransitions.slice(0, 10).map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-4 py-3">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                  t.toState === 'damaged' ? 'bg-red-100' : t.toState === 'dispatched' ? 'bg-emerald-100' : 'bg-blue-100'
                }`}>
                  {t.toState === 'damaged' ? (
                    <AlertTriangle size={14} className="text-red-600" />
                  ) : t.toState === 'dispatched' ? (
                    <CheckCircle size={14} className="text-emerald-600" />
                  ) : (
                    <ArrowRight size={14} className="text-blue-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {t.productName}
                    <span className="ml-2 text-xs text-gray-400">({t.quantity} units)</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {QC_STATE_LABELS[t.fromState || ''] || t.fromState}
                    <ArrowRight size={10} className="mx-1 inline" />
                    {QC_STATE_LABELS[t.toState || ''] || t.toState}
                    {t.reason && <span className="ml-2 text-gray-400">- {t.reason}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <Clock size={12} />
                  {formatDate(t.createdAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Plan Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Production Target</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product *</label>
                <select
                  value={newItem.productId}
                  onChange={(e) => setNewItem({ ...newItem, productId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Target Quantity *</label>
                <input
                  type="number"
                  min={1}
                  value={newItem.targetQty}
                  onChange={(e) => setNewItem({ ...newItem, targetQty: e.target.value })}
                  placeholder="0"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <p className="text-xs text-gray-400">Date: {selectedDate}</p>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                disabled={saving || !newItem.productId || !newItem.targetQty}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Add Target
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Metric Card ──────────────────────────────────────────────────────────────

function MetricCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
  placeholder,
}: {
  icon: typeof Target;
  label: string;
  value: string;
  sub: string;
  color: string;
  placeholder?: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${placeholder ? 'bg-gray-100' : 'bg-[var(--saffron-50)]'}`}>
          <Icon size={16} className={placeholder ? 'text-gray-400' : 'text-[var(--ink-0)]'} />
        </div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
      </div>
      <p className={`mt-2 text-2xl font-bold ${color}`}>{value}</p>
      <p className={`mt-0.5 text-xs ${placeholder ? 'text-[var(--saffron-600)] cursor-pointer hover:underline' : 'text-gray-400'}`}>
        {sub}
      </p>
    </div>
  );
}
